export type PhoneNumberCountry = {
  code: string;
  dialCode: string;
  label: string;
};

type PhoneNumberCountryCode = (typeof countries)[number]['code'];

/**
 * Returns the phone number country for given countryCode.
 */
export const getPhoneNumberCountry = (
  countryCode: PhoneNumberCountryCode,
): PhoneNumberCountry | undefined => {
  return countries.find(({ code }) => code === countryCode.toUpperCase());
};

/**
 * Returns all available phone number countries.
 */
export const getPhoneNumberCountries = (): PhoneNumberCountry[] => countries;

/**
 * Returns the default phone number country.
 */
export const getDefaultPhoneNumberCountry = () => countries[0];

/**
 * Guesses the phone number country based on given phone number.
 * @param phone
 */
export const guessPhoneNumberCountryFromPhoneNumber = (
  phone: string,
): PhoneNumberCountry | undefined => {
  let normalized = phone.trim();

  if (normalized.substring(0, 2) === '00') {
    normalized = `+${normalized.substring(2)}`;
  }

  return countries.find(({ dialCode }) => {
    return normalized.substring(0, dialCode.length) === dialCode;
  });
};

const countries = [
  {
    code: 'AL',
    dialCode: '+355',
    label: 'Shqipëri',
  },
  {
    code: 'AD',
    dialCode: '+376',
    label: 'Andorra',
  },
  {
    code: 'AT',
    dialCode: '+43',
    label: 'Österreich',
  },
  {
    code: 'BY',
    dialCode: '+375',
    label: 'Беларусь',
  },
  {
    code: 'BE',
    dialCode: '+32',
    label: 'België/Belgique/Belgien',
  },
  {
    code: 'BA',
    dialCode: '+387',
    label: 'Bosna i Hercegovina',
  },
  {
    code: 'BG',
    dialCode: '+359',
    label: 'България',
  },
  {
    code: 'HR',
    dialCode: '+385',
    label: 'Hrvatska',
  },
  {
    code: 'CY',
    dialCode: '+357',
    label: 'Κύπρος/Kıbrıs',
  },
  {
    code: 'CZ',
    dialCode: '+420',
    label: 'Česká republika',
  },
  {
    code: 'DK',
    dialCode: '+45',
    label: 'Danmark',
  },
  {
    code: 'EE',
    dialCode: '+372',
    label: 'Eesti',
  },
  {
    code: 'FO',
    dialCode: '+298',
    label: 'Føroyar',
  },
  {
    code: 'FI',
    dialCode: '+358',
    label: 'Suomi',
  },
  {
    code: 'FR',
    dialCode: '+33',
    label: 'France',
  },
  {
    code: 'DE',
    dialCode: '+49',
    label: 'Deutschland',
  },
  {
    code: 'GI',
    dialCode: '+350',
    label: 'Gibraltar',
  },
  {
    code: 'GR',
    dialCode: '+30',
    label: 'Ελλάδα',
  },
  {
    code: 'HU',
    dialCode: '+36',
    label: 'Magyarország',
  },
  {
    code: 'IS',
    dialCode: '+354',
    label: 'Ísland',
  },
  {
    code: 'IE',
    dialCode: '+353',
    label: 'Éire/Ireland',
  },
  {
    code: 'IT',
    dialCode: '+39',
    label: 'Italia',
  },
  {
    code: 'XK',
    dialCode: '+383',
    label: 'Kosovë/Kosovo',
  },
  {
    code: 'LV',
    dialCode: '+371',
    label: 'Latvija',
  },
  {
    code: 'LI',
    dialCode: '+423',
    label: 'Liechtenstein',
  },
  {
    code: 'LT',
    dialCode: '+370',
    label: 'Lietuva',
  },
  {
    code: 'LU',
    dialCode: '+352',
    label: 'Luxembourg/Lëtzebuerg',
  },
  {
    code: 'MT',
    dialCode: '+356',
    label: 'Malta',
  },
  {
    code: 'MD',
    dialCode: '+373',
    label: 'Moldova',
  },
  {
    code: 'MC',
    dialCode: '+377',
    label: 'Monaco',
  },
  {
    code: 'ME',
    dialCode: '+382',
    label: 'Crna Gora/Црна Гора',
  },
  {
    code: 'NL',
    dialCode: '+31',
    label: 'Nederland',
  },
  {
    code: 'MK',
    dialCode: '+389',
    label: 'Македонија',
  },
  {
    code: 'NO',
    dialCode: '+47',
    label: 'Norge/Noreg',
  },
  {
    code: 'PL',
    dialCode: '+48',
    label: 'Polska',
  },
  {
    code: 'PT',
    dialCode: '+351',
    label: 'Portugal',
  },
  {
    code: 'RO',
    dialCode: '+40',
    label: 'România',
  },
  {
    code: 'RU',
    dialCode: '+7',
    label: 'Россия',
  },
  {
    code: 'SM',
    dialCode: '+378',
    label: 'San Marino',
  },
  {
    code: 'RS',
    dialCode: '+381',
    label: 'Србија/Srbija',
  },
  {
    code: 'SK',
    dialCode: '+421',
    label: 'Slovensko',
  },
  {
    code: 'SI',
    dialCode: '+386',
    label: 'Slovenija',
  },
  {
    code: 'ES',
    dialCode: '+34',
    label: 'España',
  },
  {
    code: 'SE',
    dialCode: '+46',
    label: 'Sverige',
  },
  {
    code: 'CH',
    dialCode: '+41',
    label: 'Schweiz/Suisse/Svizzera/Svizra',
  },
  {
    code: 'TR',
    dialCode: '+90',
    label: 'Türkiye',
  },
  {
    code: 'UA',
    dialCode: '+380',
    label: 'Україна',
  },
  {
    code: 'GB',
    dialCode: '+44',
    label: 'United Kingdom',
  },
  {
    code: 'US',
    dialCode: '+1',
    label: 'United States',
  },
];
